/**
 * Hit-weight vocabulary + resolver (hit-feel Round A, Phase A).
 *
 * Implements H1 and H2 of the hit-feel decision sheet
 * (`docs/proposals/2026-08-08-hit-feel-h1-h6-decision-sheet.md`), DJ ACCEPTED
 * 2026-08-09 — architecture only. **No tuning value and no visual result is
 * approved by that sheet**, which shapes this module in two specific ways:
 *
 * 1. The damage-derived fallback delegates to the legacy `resolveIntensity`
 *    rather than introducing new thresholds, so presentation is unchanged
 *    until DJ approves tuning. `hitWeight.test.ts` locks both the delegation
 *    and the absolute expectations.
 * 2. `tap` is therefore reachable only through an *authored* weight. Choosing
 *    a damage threshold that yields `tap` is a tuning decision DJ has not
 *    made. See OPEN QUESTIONS below.
 *
 * OPEN QUESTIONS for DJ (do not resolve these in code without a ruling):
 * - Should a critical also bump the *damage-derived* fallback one tier? H2
 *   calls that path a "compatibility fallback", so it currently keeps legacy
 *   crit behaviour (jump to heavy, or epic above the medium threshold) rather
 *   than the one-tier bump. The one-tier bump applies to authored weights.
 * - What damage, if any, should resolve to `tap`?
 */

import { resolveIntensity } from '../constants/CombatFeedbackConfig';

/** The authored/runtime hit-weight vocabulary (H1). */
export type HitWeight = 'tap' | 'light' | 'medium' | 'heavy' | 'epic';

/**
 * The pre-H1 runtime vocabulary. Accepted at adapter boundaries only —
 * `massive` normalizes to `epic` and is never emitted as new authority.
 */
export type LegacyHitWeight = 'light' | 'medium' | 'heavy' | 'massive';

/** Ascending tier order. Index position defines what "one tier" means. */
export const HIT_WEIGHT_ORDER = ['tap', 'light', 'medium', 'heavy', 'epic'] as const satisfies readonly HitWeight[];

/** Adapter boundary: accept legacy labels, emit only current vocabulary. */
export function normalizeHitWeight(weight: HitWeight | LegacyHitWeight): HitWeight {
  return weight === 'massive' ? 'epic' : weight;
}

/** Raise a weight by exactly one tier, capped at `epic` (H2 critical bump). */
export function bumpHitWeight(weight: HitWeight): HitWeight {
  const index = HIT_WEIGHT_ORDER.indexOf(weight);
  return HIT_WEIGHT_ORDER[Math.min(index + 1, HIT_WEIGHT_ORDER.length - 1)];
}

export interface HitWeightInput {
  /** Explicit authored weight. When present it wins over damage entirely. */
  readonly authoredWeight?: HitWeight | LegacyHitWeight | null;
  readonly damage: number;
  readonly critical: boolean;
}

/**
 * Resolve the weight for one hit.
 *
 * Authored weight wins; a critical bumps it one tier, capped at `epic`.
 * With no authored weight, fall back to the legacy damage-derived buckets so
 * current presentation is preserved exactly.
 */
export function resolveHitWeight({ authoredWeight, damage, critical }: HitWeightInput): HitWeight {
  if (authoredWeight) {
    const base = normalizeHitWeight(authoredWeight);
    return critical ? bumpHitWeight(base) : base;
  }

  return normalizeHitWeight(resolveIntensity(damage, critical));
}

/**
 * Per-hit correlation id (H4).
 *
 * Hit ownership is currently a 700ms FIFO keyed by `(targetId, abilityId)`,
 * which is only correct while same-key confirmations retain delivery order.
 * A stable id per hit is the prerequisite for relaxing that assumption and
 * for reporting requested-vs-applied truth against a single hit.
 */
const CORRELATION_NONCE = Math.random().toString(36).slice(2, 10);
let correlationCounter = 0;

export function newHitCorrelationId(): string {
  correlationCounter += 1;
  return `hit_${CORRELATION_NONCE}_${correlationCounter}`;
}
